import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'usehooks-ts'
import { useSearchParams } from 'react-router-dom'

// Layout components
import SearchLayout from '../layouts/SearchLayout'

// User Management components
import UserManagementFilters from './UserManagementFilters'
import UserManagementSearch from './UserManagementSearch'
import UserManagementChips from './UserManagementChips'
import UserManagementResults from './UserManagementResults'

// Hooks and services
import useUserManagementSearch from './useUserManagementSearch'

// Redux actions and selectors
import {
  clearAllFilters,
  getToolFilters,
  getStatusFilters,
  getSorting,
} from '../../store/userManagement/userManagementSlice'

// API service
import {
  useGetClientAdminUsersQuery,
  UserApplicationData,
} from '../../store/userManagement/userManagementSearchService'

import { RsmPaginationPageSizes } from '../../rsmCoreComponents/components/RsmPagination/RsmPagination'

/**
 * UserManagementLayout - Main orchestrator component for User Management feature
 *
 * This component serves as the primary container and data coordinator for the User Management
 * interface. It follows the established InvoicesLayout pattern and integrates with the
 * SearchLayout component to provide a consistent user experience.
 *
 * Key Responsibilities:
 * - Feature flag validation for CLIENT_ADMIN access control
 * - Orchestrates data fetching for filters and user data using RTK Query
 * - Manages debounced search with 500ms delay for performance optimization
 * - Sets default filters to Active and Provisioned status on mount
 * - Coordinates filter state management and persistence
 * - Handles component lifecycle with proper cleanup on unmount
 * - Provides responsive layout structure using SearchLayout
 *
 * Features:
 * - Sophisticated empty state handling when no users exist
 * - Filter persistence across page navigation and sessions
 * - Accessibility compliance with WCAG 2.1 AA standards
 * - Mobile responsive design with theme-aware breakpoints
 * - Comprehensive error handling with user-friendly messaging
 *
 * Component Architecture:
 * - Uses SearchLayout as the structural foundation
 * - Composes child components through slot-based pattern
 * - Integrates with Redux for state management
 * - Leverages RTK Query for efficient data fetching
 *
 * Integration Points:
 * - API endpoints: /api/v1/clientadmin/filters and /api/v1/clientadmin/users
 * - Redux store: userManagement slice for filter and pagination state
 * - URL parameters: Filter state persistence through search params
 */
const UserManagementLayout: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  // Local state for search management
  const [searchValue, setSearchValue] = useState<string>('')
  const debouncedSearch = useDebounce<string>(searchValue, 500) // 500ms debounce as specified

  // User Management search hook - orchestrates all data operations
  const {
    // Filter API state
    filterIsSuccess,
    filterIsError,
    filterData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    filterError,

    // Users API state
    // usersIsLoading, // Intentionally omitted - not used in this implementation
    usersIsError,
    usersData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    usersError,

    // Pagination controls
    pageInfo,
    setUserPageSize,
    toPage,

    // Filter management
    clearFilters,
    clearCategoryFilter,
    // changeFilterValue, // Intentionally omitted - not used in this implementation

    // Search controls
    // requestSearch, // Intentionally omitted - not used in this implementation
    changeFilterAndSearch,
    changeSearchFilterAndSearch,

    // Sorting
    SortUserData,
  } = useUserManagementSearch()

  // RTK Query hook for users data with current filters
  const { data: currentUsersData } = useGetClientAdminUsersQuery()

  // Get current filter and sort state from Redux
  const toolFilters = useSelector(getToolFilters)
  const statusFilters = useSelector(getStatusFilters)
  const sorting = useSelector(getSorting)

  // Client-side filtering, sorting with memoization for performance
  const filteredAndSortedUsers = useMemo(() => {
    let result = [...(usersData || currentUsersData || [])]

    // Apply search filter (minimum 2 characters, case-insensitive)
    if (debouncedSearch && debouncedSearch.length >= 2) {
      const searchLower = debouncedSearch.toLowerCase()
      result = result.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.tool?.toLowerCase().includes(searchLower),
      )
    }

    // Apply tool filters (OR logic within dimension)
    if (toolFilters.length > 0) {
      result = result.filter((user) => toolFilters.includes(user.tool))
    }

    // Apply status filters (OR logic within dimension)
    if (statusFilters.length > 0) {
      result = result.filter((user) => statusFilters.includes(user.status))
    }

    // Apply sorting (primary by sortBy column, secondary by Tool for tiebreaker)
    result.sort((a, b) => {
      let compareResult = 0
      const { sortBy, sortDirection: direction } = sorting

      // Primary sort by selected column
      const sortKey = sortBy as keyof UserApplicationData
      const aVal = a[sortKey] || ''
      const bVal = b[sortKey] || ''
      if (aVal < bVal) compareResult = -1
      if (aVal > bVal) compareResult = 1

      // Secondary sort by Tool if primary values are equal
      if (compareResult === 0 && sortBy !== 'tool') {
        if (a.tool < b.tool) compareResult = -1
        if (a.tool > b.tool) compareResult = 1
      }

      return direction === 'asc' ? compareResult : -compareResult
    })

    return result
  }, [
    usersData,
    currentUsersData,
    debouncedSearch,
    toolFilters,
    statusFilters,
    sorting,
  ])

  /**
   * Handle pagination changes - delegates to search hook
   * Manages page size and page number updates with search refresh
   */
  const handlePageChange = (
    pageNumber: number,
    pageSize: RsmPaginationPageSizes,
  ) => {
    setUserPageSize(pageSize)
    toPage(pageNumber)
  }

  /**
   * Handle sorting changes - delegates to search hook
   * Updates sort column and direction with data refresh
   */
  const handleSorting = (sortBy: string, isAscending: 'asc' | 'desc') => {
    SortUserData(sortBy, isAscending)
    toPage(1) // Reset to first page when sorting changes
  }

  /**
   * Handle filter and search operations
   * Wraps the search hook's filter change functionality
   */
  const handleFilterAndSearch = (filterType: string, value: string) => {
    changeFilterAndSearch(filterType, value)
  }

  /**
   * Handle clear all filters action
   * Resets all filters to initial state and triggers search refresh
   */
  const handleClearAllFilters = () => {
    dispatch(clearAllFilters())
    clearFilters()
  }

  /**
   * Handle category-specific filter clearing
   * Clears specific filter categories (tools, status, search)
   */
  const handleClearCategoryFilter = (category: string) => {
    clearCategoryFilter(category)
  }

  /**
   * Effect: Handle debounced search execution
   * Triggers search API call when debounced search value changes
   */
  useEffect(() => {
    if (debouncedSearch !== undefined) {
      changeSearchFilterAndSearch(debouncedSearch)
    }
  }, [debouncedSearch, changeSearchFilterAndSearch])

  /**
   * Effect: URL parameter handling for client ID
   * Processes clientId query parameter if present
   */
  useEffect(() => {
    const clientIdParam = searchParams.get('clientId')
    if (clientIdParam) {
      // Handle client ID filtering if needed - following InvoicesLayout pattern
      const clientId = Number(clientIdParam)
      if (!Number.isNaN(clientId) && clientId > 0) {
        // Process client ID filter if this feature is needed
        // Currently not implemented as per mock data strategy
      }
    }
  }, [searchParams])

  /**
   * Main render - SearchLayout composition with all child components
   * Follows the established pattern from InvoicesLayout for consistency
   */
  return (
    <SearchLayout
      title={t('UserManagement.PageTitle')}
      drawerDialogSlot={
        <UserManagementFilters
          filterData={filterData}
          isFilterError={filterIsError}
          isFilterSuccess={filterIsSuccess}
          changeFilterAndSearch={handleFilterAndSearch}
          clearCategoryFilter={handleClearCategoryFilter}
        />
      }
      drawerDialogTitle={t('UserManagement.FiltersTitle', 'Filters')}
      dialogSecondaryButtonClickHandler={handleClearAllFilters}
      dialogSecondaryButtonText={t(
        'UserManagement.ClearAllFilters',
        'Clear All',
      )}
      searchSlot={<UserManagementSearch setFilter={setSearchValue} />}
      chipsSlot={
        <UserManagementChips
          changeFilterAndSearch={handleFilterAndSearch}
          clearAllFilters={handleClearAllFilters}
        />
      }>
      <UserManagementResults
        usersData={filteredAndSortedUsers}
        isUsersError={usersIsError}
        pageNumber={pageInfo.pageNumber}
        pageSize={pageInfo.pageSize}
        onPageChange={handlePageChange}
        onSorting={handleSorting}
        sortColumn={sorting.sortBy}
        sortDirection={sorting.sortDirection}
      />
    </SearchLayout>
  )
}

export default UserManagementLayout
